/**
 * TwilioDeviceContext — Phase 4 etape 6.
 *
 * Hold les 10 etats lies au Twilio Voice Web SDK + le useEffect d'init
 * du SDK (token, register, on-events). Le hook useTwilioDeviceInit
 * gere le cycle de vie du Twilio Voice SDK et expose un callback
 * onIncomingCallAccepted pour que le DialerProviderInner reagisse a
 * un appel entrant accepte (set connectedContact, etc).
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useRef, useEffect, useCallback, type MutableRefObject } from 'react';
import type { DeviceErrorCode } from '@/services/dialer/telephonyIssue';
import { useTranslation } from 'react-i18next';
// #23 -- lecture du reglage son sans passer par React (pas de cycle).
import { sonEstActif } from '@/state/soundPrefs';
import { playLineOpenChime } from '@/state/lineOpenChime';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
// @ts-ignore — JS module
import { api } from '@/api/api-client';
import * as TwilioCalls from '@/services/dialer/TwilioCallService';
import { ConferenceLeg, ConferenceRoomClient, isBusyWithAnotherCall } from '@/services/dialer/conferenceRoom';
import { measureHeard } from '@/services/telephony/heardTiming';

interface AvailableDevices {
  inputs: any[];
  outputs: any[];
}

/** Statut precis de la permission micro pour distinguer "pas encore demande"
 * de "refuse" ou "indisponible" — sert au banner bloquant + CTA contextuel. */
export type MicPermissionStatus = 'unknown' | 'granted' | 'denied' | 'unsupported';

export interface TwilioDeviceValue {
  micPermissionGranted: boolean;
  setMicPermissionGranted: (v: boolean) => void;
  micPermissionStatus: MicPermissionStatus;
  /** Retente getUserMedia (CTA "Autoriser le micro"). */
  retryMicPermission: () => Promise<void>;

  deviceReady: boolean;
  setDeviceReady: (v: boolean) => void;
  deviceRef: any;
  setDeviceRef: (v: any) => void;
  deviceState: string;
  setDeviceState: (v: string) => void;
  deviceError: string | null;
  setDeviceError: (v: string | null) => void;
  /**
   * Why the device is in error, as a code the UI translates (T-d1). The raw
   * deviceError text ("Internal server error"…) is for logs only.
   */
  deviceErrorCode: DeviceErrorCode | null;
  setDeviceErrorCode: (v: DeviceErrorCode | null) => void;
  deviceIdentity: string | null;
  setDeviceIdentity: (v: string | null) => void;

  incomingCallConnection: any;
  setIncomingCallConnection: (v: any) => void;

  audioInputDevice: string;
  setAudioInputDevice: (v: string) => void;
  audioOutputDevice: string;
  setAudioOutputDevice: (v: string) => void;
  availableDevices: AvailableDevices;
  setAvailableDevices: (v: AvailableDevices) => void;

  /** Ref synchrone pour bloquer le double-init en StrictMode. */
  deviceInitializedRef: MutableRefObject<boolean>;
}

const TwilioDeviceContext = createContext<TwilioDeviceValue | null>(null);

export function TwilioDeviceProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation(['dialer']);
  const [micPermissionGranted, setMicPermissionGranted] = useState(false);
  const [micPermissionStatus, setMicPermissionStatus] = useState<MicPermissionStatus>('unknown');
  // Tente getUserMedia. withToast=false au mount initial pour ne pas spammer
  // un utilisateur qui n'a pas encore decide ; withToast=true sur le CTA
  // "Autoriser le micro" pour confirmer succes/echec.
  const requestMicPermission = useCallback(async (withToast: boolean) => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      setMicPermissionStatus('unsupported');
      setMicPermissionGranted(false);
      if (withToast) toast.error(t('dialer:twilioDevice.micUnsupported'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicPermissionGranted(true);
      setMicPermissionStatus('granted');
      if (withToast) toast.success(t('dialer:twilioDevice.micGranted'));
    } catch (_e) {
      setMicPermissionGranted(false);
      // NotAllowedError = refus utilisateur ou refus systeme ; NotFoundError =
      // aucun device audio detecte ; tout le reste = on traite comme "denied".
      setMicPermissionStatus('denied');
      if (withToast) {
        toast.error(t('dialer:twilioDevice.micBlocked'));
      }
    }
  }, []);
  const retryMicPermission = useCallback(() => requestMicPermission(true), [requestMicPermission]);
  // Premier essai au mount du provider, silencieux.
  const initialMicTriedRef = useRef(false);
  useEffect(() => {
    if (initialMicTriedRef.current) return;
    initialMicTriedRef.current = true;
    requestMicPermission(false);
  }, [requestMicPermission]);
  const [deviceReady, setDeviceReady] = useState(false);
  const [deviceRef, setDeviceRef] = useState<any>(null);
  const [deviceState, setDeviceState] = useState('unregistered');
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [deviceErrorCode, setDeviceErrorCode] = useState<DeviceErrorCode | null>(null);
  const [deviceIdentity, setDeviceIdentity] = useState<string | null>(null);
  const [incomingCallConnection, setIncomingCallConnection] = useState<any>(null);
  const [audioInputDevice, setAudioInputDevice] = useState('');
  const [audioOutputDevice, setAudioOutputDevice] = useState('');
  const [availableDevices, setAvailableDevices] = useState<AvailableDevices>({ inputs: [], outputs: [] });
  const deviceInitializedRef = useRef(false);

  // Audio devices enumeration — quand la permission mic est accordee, on
  // liste les inputs/outputs disponibles et on set des defaults.
  useEffect(() => {
    if (!micPermissionGranted) return;
    (async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const inputs = devices.filter(d => d.kind === 'audioinput');
        const outputs = devices.filter(d => d.kind === 'audiooutput');
        setAvailableDevices({ inputs, outputs });
        if (!audioInputDevice && inputs.length > 0 && inputs[0]) setAudioInputDevice(inputs[0].deviceId);
        if (!audioOutputDevice && outputs.length > 0 && outputs[0]) setAudioOutputDevice(outputs[0].deviceId);
      } catch (_e) { /* navigator.mediaDevices may be unavailable */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micPermissionGranted]);

  const value: TwilioDeviceValue = {
    micPermissionGranted, setMicPermissionGranted,
    micPermissionStatus,
    retryMicPermission,
    deviceReady, setDeviceReady,
    deviceRef, setDeviceRef,
    deviceState, setDeviceState,
    deviceError, setDeviceError,
    deviceErrorCode, setDeviceErrorCode,
    deviceIdentity, setDeviceIdentity,
    incomingCallConnection, setIncomingCallConnection,
    audioInputDevice, setAudioInputDevice,
    audioOutputDevice, setAudioOutputDevice,
    availableDevices, setAvailableDevices,
    deviceInitializedRef,
  };

  return (
    <TwilioDeviceContext.Provider value={value}>
      {children}
    </TwilioDeviceContext.Provider>
  );
}

export function useTwilioDevice(): TwilioDeviceValue {
  const v = useContext(TwilioDeviceContext);
  if (!v) throw new Error('useTwilioDevice must be used within TwilioDeviceProvider');
  return v;
}

// ─── Init hook ───────────────────────────────────────────────────────────────

export interface IncomingCallContext {
  callId: string;
  prospectName: string;
  prospectPhone: string;
  prospectCompany: string;
  enrichedContact: any;
}

export interface UseTwilioDeviceInitArgs {
  callerEmail: string | null | undefined;
  /**
   * Callback declenche apres call.on('accept') ET apres l'enrichissement BDD,
   * avec le contact enrichi. Le DialerProviderInner s'en sert pour set
   * connectedContact + showCallSheet + push ENRICH_CONTACT a la machine.
   */
  onIncomingCallAccepted: (ctx: IncomingCallContext) => void;
  /** Callback declenche quand un call entrant disconnect. */
  onIncomingCallDisconnected: (callId: string, durationMs: number) => void;
  /**
   * Callback declenche au moment ou un appel externe (non bridge par notre
   * propre /parallel-dial — typiquement un humain qui appelle un numero
   * Twilio du tenant) commence a sonner cote browser. Sert au DialerProvider
   * pour stopper immediatement le power-mode et raccrocher les outbound en
   * sonnerie afin d'eviter une double-conversation. Recoit le numero From.
   */
  onExternalIncomingRinging?: (fromNumber: string) => void;
}

/**
 * Cycle de vie complet du Twilio Voice Web SDK :
 * - Fetch du token via /generateCallerToken
 * - Instanciation du Device
 * - Branchement des handlers (registered, error, tokenWillExpire, incoming)
 * - Refresh token toutes les 50 minutes
 * - Spawn de la callMachine via window.__callPool sur 'incoming'
 * - Update ParallelCall en BDD sur accept + disconnect
 *
 * Le useEffect est gere ici. Le DialerProviderInner ne fournit que les
 * callbacks metier specifiques (set connectedContact, etc).
 */
export function useTwilioDeviceInit({
  callerEmail,
  onIncomingCallAccepted,
  onIncomingCallDisconnected,
  onExternalIncomingRinging,
}: UseTwilioDeviceInitArgs): void {
  const { t } = useTranslation(['dialer']);
  const queryClient = useQueryClient();
  const {
    micPermissionGranted,
    setDeviceReady, setDeviceRef, setDeviceState, setDeviceError, setDeviceErrorCode, setDeviceIdentity,
    setIncomingCallConnection,
    deviceIdentity,
    deviceInitializedRef,
  } = useTwilioDevice();

  // Reset deviceInitializedRef quand le caller change (logout / changement de session).
  useEffect(() => {
    return () => { deviceInitializedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callerEmail]);

  useEffect(() => {
    if (!callerEmail || !micPermissionGranted) return;
    if (deviceInitializedRef.current) return;

    let cancelled = false;
    let tokenRefreshInterval: ReturnType<typeof setInterval> | null = null;
    let removeDialerEventListener: (() => void) | null = null;

    const initDevice = async () => {
      try {
        setDeviceState('registering');
        setDeviceError(null);
        setDeviceErrorCode(null);
        // 2026-06-04 (E2E tests) — Si window.__MockDevice est expose par
        // la fixture Playwright, on l'utilise au lieu du vrai SDK Twilio.
        // Pas de chargement reseau, pas de WebRTC, mais l'API publique
        // est identique (on/register/audio/calls).
        const MockDevice = (window as any).__MockDevice;
        const { Device } = MockDevice
          ? { Device: MockDevice }
          : await import('@twilio/voice-sdk');
        if (cancelled) return;
        const sanitize = (str: string) => String(str || '').replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase();
        const identity = `caller_${sanitize(callerEmail)}`.slice(0, 121);

        let tokenRes: any;
        try {
          const response = await api.functions.invoke('generateCallerToken', { identity });
          tokenRes = response.data;
        } catch (error: any) {
          if (error?.response?.status === 401 || error?.status === 401) {
            try { localStorage.removeItem('revcall_caller'); } catch (_e) {}
            toast.error(t('dialer:twilioDevice.sessionExpired'));
            setTimeout(() => { window.location.href = '/'; }, 1000);
            return;
          }
          throw error;
        }

        if (!tokenRes?.token) throw new Error(tokenRes?.error || 'Failed to get Twilio token');
        // Only the run that keeps the device may publish its identity: a run
        // cancelled while its token request was in flight would otherwise
        // overwrite it, and dials would carry an identity no device holds
        // (2026-10-07: the room was joined as one identity, dials sent as
        // another, so every answered prospect took the classic flow and was cut).
        if (cancelled) return;
        if (tokenRes.identity) { setDeviceIdentity(tokenRes.identity); (window as any).__twilioDeviceIdentity = tokenRes.identity; }

        // ADR-046: the SIP client (JsSIP) is only loaded when calls go through
        // our Asterisk, so the Twilio path does not carry it.
        const AsteriskDevice = tokenRes.provider === 'asterisk' && tokenRes.sip
          ? (await import('@/services/telephony/AsteriskDevice')).AsteriskDevice
          : null;
        if (cancelled) return;

        const w = window as any;
        if (w.__twilioDevice) { try { w.__twilioDevice.destroy(); } catch (_e) {} w.__twilioDevice = null; }

        // ADR-046: calls through our Asterisk. The token route then answers
        // SIP credentials and the line is a WebRTC SIP client, not Twilio's.
        const device: any = AsteriskDevice
          ? new AsteriskDevice(tokenRes.sip)
          : new Device(tokenRes.token, {
          logLevel: 1,
          codecPreferences: ['opus', 'pcmu'] as any,
          closeProtection: true,
          // The standing conference keeps the browser "busy" for the whole
          // session; with the SDK default, every inbound call would then be
          // ignored. Busy is decided in the 'incoming' handler instead
          // (isBusyWithAnotherCall): ignored during a conversation, exactly as
          // the SDK did, accepted when the browser only sits in its conference.
          allowIncomingWhileBusy: true,
          sounds: {
            incoming: false, outgoing: false, disconnect: false,
            dtmf1: false, dtmf2: false, dtmf3: false, dtmf4: false, dtmf5: false,
            dtmf6: false, dtmf7: false, dtmf8: false, dtmf9: false, dtmf0: false,
            dtmfS: false, dtmfH: false,
          } as any,
        });
        if (cancelled) { device.destroy(); return; }
        w.__twilioDevice = device;

        const refreshToken = async () => {
          try {
            const { data }: { data: any } = await api.functions.invoke('generateCallerToken', {
              identity,
              // The device's own identity: the state captured by this closure
              // is the one from before the first token, so the server used to
              // issue a new identity at every refresh.
              existingIdentity: tokenRes.identity || undefined,
            });
            if (cancelled || !data?.token || !w.__twilioDevice) return;
            w.__twilioDevice.updateToken(data.provider === 'asterisk' ? data.sip : data.token);
            if (data.identity && data.identity !== tokenRes.identity) {
              tokenRes.identity = data.identity;
              setDeviceIdentity(data.identity);
              w.__twilioDeviceIdentity = data.identity;
            }
          } catch (_e) { /* silent */ }
        };

        device.on('registered', () => {
          if (cancelled) return;
          console.log('[Device] registered identity=', tokenRes.identity);
          deviceInitializedRef.current = true;
          setDeviceReady(true);
          setDeviceRef(device);
          setDeviceState('registered');
          setDeviceError(null);
          setDeviceErrorCode(null);
          // 2026-06-04 (Kevin live demo bug #5) — La config sounds.incoming:false
          // au boot du Device n'est pas fiable (cf. twilio-voice.js GitHub
          // issue #312). On force la coupure du ringtone par defaut Twilio
          // via l'API runtime device.audio.incoming(false). Sans ca, le
          // ringtone agressif joue malgre tout sur appel inbound externe.
          // Ref doc : https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice/device-audio
          try {
            if (device.audio?.incoming) {
              device.audio.incoming(false);
              console.log('[Device] runtime audio.incoming(false) applied — ringtone Twilio coupe');
            }
            if (device.audio?.outgoing) device.audio.outgoing(false);
            if (device.audio?.disconnect) device.audio.disconnect(false);
          } catch (e: any) {
            console.warn('[Device] failed to apply runtime audio mutes', e?.message);
          }
          // 2026-06-05 — Heartbeat de presence. Tant que le device est registered,
          // on pingue /api/twilio/heartbeat toutes les 20s pour que le serveur
          // sache que ce caller est joignable MAINTENANT. Sinon le voice-handler
          // bascule sur le voicemail "Aucun conseiller disponible" alors qu'on
          // est en ligne (cf. bug Kevin 2026-06-05 — repondeur sur inbound).
          const sendHeartbeat = () => {
            try {
              fetch('/api/twilio/heartbeat', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ identity: tokenRes.identity }),
              }).catch(() => {});
            } catch (_e) { /* silent */ }
          };
          sendHeartbeat();
          if ((w as any).__twilioHeartbeatTimer) clearInterval((w as any).__twilioHeartbeatTimer);
          (w as any).__twilioHeartbeatTimer = setInterval(sendHeartbeat, 20_000);
        });
        device.on('registering', () => { console.log('[Device] registering...'); setDeviceState('registering'); });
        device.on('unregistered', () => {
          if (cancelled) return;
          console.warn('[Device] unregistered — incoming calls will NOT reach this browser. Auto-retry register in 3s.');
          setDeviceReady(false);
          setDeviceState('unregistered');
          if ((w as any).__twilioHeartbeatTimer) {
            clearInterval((w as any).__twilioHeartbeatTimer);
            (w as any).__twilioHeartbeatTimer = null;
          }
          setTimeout(() => { if (w.__twilioDevice && !cancelled) try { w.__twilioDevice.register(); } catch (_e) {} }, 3000);
        });
        device.on('error', (err: any) => {
          if (cancelled) return;
          console.error('[Device] error code=', err.code, 'message=', err.message, 'twilioError=', err.twilioError?.code);
          setDeviceError(err.message);
          setDeviceErrorCode('TEL_OFFLINE');
          if (err.code === 20104 || err.code === 31205) {
            console.log('[Device] token-related error → refresh token + re-register');
            refreshToken().then(() => { try { device.register(); } catch (_e) {} });
          } else { setDeviceState('error'); }
        });
        device.on('tokenWillExpire', refreshToken);

        // What happens once a prospect is bridged to this browser: the call
        // machine, the ParallelCall update, the contact card. Shared by the
        // classic flow (an incoming Twilio call per prospect) and by the
        // standing conference (a ConferenceLeg per prospect, 2026-10-06).
        const wireProspectCall = (call: any, info: { callId: string; prospectName: string; prospectPhone: string; prospectCompany: string; incomingAt: number }) => {
          const { callId, prospectName, prospectPhone, prospectCompany, incomingAt } = info;
          call.accept();
          w.__twilioActiveCall = call;
          console.log('[Device] call.accept() called for callId=', callId);
          call.on('disconnect', () => {
            if (w.__twilioActiveCall === call) w.__twilioActiveCall = null;
          });
          call.on('accept', async () => {
            console.log('[Device] call ACCEPTED bridge-ms=', Date.now() - incomingAt, 'callId=', callId);
            // Push TWILIO_ANSWERED a la machine (50ms vs 500-2000ms du SSE).
            // Si la machine n'existe pas encore (race SSE), on la spawn ici.
            try {
              let actor = w.__callPool?.get(callId);
              if (!actor && w.__callPool) {
                actor = w.__callPool.spawn({
                  callId,
                  prospectId: '',
                  phone: prospectPhone,
                  initialContact: { callId, prospect_name: prospectName, prospect_phone: prospectPhone, prospect_company: prospectCompany },
                });
                actor?.send({ type: 'DIAL_START', callId, prospectId: '', phone: prospectPhone });
                actor?.send({ type: 'TWILIO_RINGING' });
              }
              if (actor) actor.send({ type: 'TWILIO_ANSWERED', bridgedAt: Date.now() });
            } catch (_e) {}
            // Enrichir avec TOUS les champs du prospect.
            let enrichedContact: any = {
              callId,
              prospectName, prospectPhone, prospectCompany,
              prospect_name: prospectName,
              prospect_phone: prospectPhone,
              prospect_company: prospectCompany,
            };
            // 2026-10-06: the card opens NOW with what we already know (name,
            // number, company); the details below fill it in when they
            // arrive. It used to wait for three requests in a row (status
            // update, call read, prospect search) before showing anything.
            onIncomingCallAccepted({ callId, prospectName, prospectPhone, prospectCompany, enrichedContact });
            // Measurement (2026-10-07): when call:bridged arrived; the card
            // reports its own paint (ProspectDetailPanel), and the details
            // below their arrival.
            const measured = !!(call as { isConferenceLeg?: boolean }).isConferenceLeg;
            // The prospect is heard from now: a soft chime says "speak now".
            if (measured) playLineOpenChime();
            if (measured) { w.__cardReceivedAt = w.__cardReceivedAt || {}; w.__cardReceivedAt[callId] = incomingAt; }
            // The standing conference already set 'connected_to_agent' on the
            // server; the classic flow still reports it from here.
            if (!(call as { isConferenceLeg?: boolean }).isConferenceLeg) {
              api.entities.ParallelCall.update(callId, { call_status: 'connected_to_agent' })
                .then(() => queryClient.invalidateQueries({ queryKey: ['parallel-calls'] }))
                .catch(() => undefined);
            } else {
              queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
            }
            try {
              const parallelCall: any = await api.entities.ParallelCall.get(callId);
              if (parallelCall) {
                enrichedContact = {
                  ...enrichedContact,
                  prospect_id: parallelCall.prospect_id,
                  prospect_name: parallelCall.prospect_name || prospectName,
                  prospect_phone: parallelCall.prospect_phone || prospectPhone,
                  prospect_email: parallelCall.prospect_email || '',
                  prospect_company: parallelCall.prospect_company || prospectCompany,
                  prospect_title: parallelCall.prospect_title || '',
                  linkedin_url: parallelCall.linkedin_url || '',
                  website: parallelCall.website || '',
                  notes: parallelCall.notes || '',
                };
                if (parallelCall.prospect_phone && (!parallelCall.prospect_email || !parallelCall.linkedin_url)) {
                  try {
                    const matches: any[] = await api.entities.ProspectQueue.filter({ prospect_phone: parallelCall.prospect_phone });
                    const p: any = matches?.[0];
                    if (p) {
                      enrichedContact.prospect_email = enrichedContact.prospect_email || p.prospect_email || '';
                      enrichedContact.prospect_title = enrichedContact.prospect_title || p.prospect_title || '';
                      enrichedContact.linkedin_url   = enrichedContact.linkedin_url   || p.linkedin_url   || '';
                      enrichedContact.website        = enrichedContact.website        || p.website        || '';
                      enrichedContact.prospect_company = enrichedContact.prospect_company || p.prospect_company || '';
                    }
                  } catch (e: any) { console.warn('[Device] enrich from ProspectQueue failed', e?.message); }
                }
              }
            } catch (e: any) { console.warn('[Device] enrich from ParallelCall failed', e?.message); }
            if (measured) TwilioCalls.reportCardTiming(callId, incomingAt, Date.now(), 'enriched');
            // Notifie le DialerProviderInner — il fera set connectedContact + showCallSheet
            // + push ENRICH_CONTACT a la machine.
            onIncomingCallAccepted({
              callId,
              prospectName,
              prospectPhone,
              prospectCompany,
              enrichedContact,
            });
            try {
              const actor = w.__callPool?.get(callId);
              if (actor) actor.send({ type: 'ENRICH_CONTACT', enrichedContact });
            } catch (_e) {}
          });
          call.on('disconnect', async () => {
            const totalMs = Date.now() - incomingAt;
            console.log('[Device] call DISCONNECTED total-ms=', totalMs, 'callId=', callId);
            try {
              const actor = w.__callPool?.get(callId);
              if (actor) actor.send({ type: 'HANGUP_REMOTE', durationMs: totalMs });
            } catch (_e) {}
            try { await api.entities.ParallelCall.update(callId, { call_status: 'completed', ended_at: new Date().toISOString() }); } catch (_e) {}
            queryClient.invalidateQueries({ queryKey: ['parallel-calls'] });
            onIncomingCallDisconnected(callId, totalMs);
          });
        };

        // Standing conference (ConferenceRoomService). Off unless the server
        // says it is switched on for this caller: ensure() then answers false
        // and every call takes the classic flow above.
        const roomClient = new ConferenceRoomClient({
          status: async (roomIdentity: string) => {
            const res: any = await api.functions.invoke('twilioConferenceRoom', { identity: roomIdentity });
            return { enabled: !!res?.data?.enabled, ready: !!res?.data?.ready };
          },
          device: () => w.__twilioDevice ?? null,
          identity: () => tokenRes.identity ?? null,
        });
        w.__revcallRoom = roomClient;
        const onDialerEvent = (e: Event) => {
          const { name, payload } = ((e as CustomEvent).detail || {}) as { name?: string; payload?: any };
          if (name === 'call:bridged' && payload?.callId && payload?.externalCallId) {
            const current = w.__conferenceLeg as ConferenceLeg | null | undefined;
            if (current && !current.isEnded() && current.callId === payload.callId) return; // repeated event
            const leg = new ConferenceLeg(payload.callId, payload.externalCallId, () => roomClient.roomCall(), (sid: string) => TwilioCalls.hangupCall(sid));
            w.__conferenceLeg = leg;
            console.log('[ConferenceRoom] prospect bridged callId=', payload.callId);
            // Measurement (2026-10-26): when the prospect's voice reaches this browser.
            void measureHeard(payload.callId, roomClient.roomCall(), Date.now());
            wireProspectCall(leg, {
              callId: payload.callId,
              prospectName: payload.prospectName || 'Appelant',
              prospectPhone: payload.prospectPhone || '',
              prospectCompany: payload.prospectCompany || '',
              incomingAt: Date.now(),
            }); // wireProspectCall accepts the leg, as it does a real call
          } else if ((name === 'call:completed' || name === 'call:voicemail') && payload?.callId) {
            const leg = w.__conferenceLeg as ConferenceLeg | null | undefined;
            if (leg && leg.callId === payload.callId) {
              leg.endedRemotely();
              w.__conferenceLeg = null;
            }
          }
        };
        window.addEventListener('revcall:dialer-event', onDialerEvent);
        removeDialerEventListener = () => window.removeEventListener('revcall:dialer-event', onDialerEvent);

        device.on('incoming', (call: any) => {
          if (isBusyWithAnotherCall(call, w.__twilioDevice, roomClient.roomCall(), w.__conferenceLeg)) {
            console.log('[Device] busy with a conversation, incoming ignored');
            try { call.ignore(); } catch (_e) { /* already gone */ }
            return;
          }
          const callId = call.customParameters?.get('callId');
          const prospectName = call.customParameters?.get('prospectName') || 'Appelant';
          const prospectPhone = call.customParameters?.get('prospectPhone') || '';
          const prospectCompany = call.customParameters?.get('prospectCompany') || '';
          const incomingAt = Date.now();
          console.log('[Device] INCOMING call', { callId, prospectName, prospectPhone, prospectCompany, customParams: call.customParameters ? Array.from(call.customParameters.entries() as any) : null });
          if (callId) {
            wireProspectCall(call, { callId, prospectName, prospectPhone, prospectCompany, incomingAt });
          } else {
            // Appel externe entrant sur un numero Twilio du tenant (le TwiML
            // /voice-handler nous l'a route via <Dial><Client>caller_xxx avec
            // des Parameters custom prospectName / prospectPhone).
            // Notifie immediatement le DialerProviderInner pour qu'il coupe
            // les outbound en cours et stoppe le power-mode — sinon le
            // browser pourrait connecter un outbound qui sonne en parallele
            // au moment ou l'agent decroche l'entrant (double conversation).
            const cleanProspectPhone = call.customParameters?.get?.('prospectPhone') || '';
            const cleanProspectName = call.customParameters?.get?.('prospectName') || '';
            const rawFrom = String(call.parameters?.From ?? '');
            // Le `From` Twilio est typiquement "sip:+33616266395@sip.zadarma.com"
            // (ressemble a un email a l'ecran). On colle un `From` propre dans
            // call.parameters pour que tout consommateur tombe sur du E.164.
            try {
              if (cleanProspectPhone && call.parameters) {
                call.parameters.From = cleanProspectPhone;
              }
            } catch (_e) {}
            // Pareil pour les customParameters cote downstream.
            const enrichedFromForUi = cleanProspectPhone || rawFrom;
            try { onExternalIncomingRinging?.(enrichedFromForUi); } catch (e) { console.warn('[Device] onExternalIncomingRinging cb failed', e); }
            // On expose un objet "annote" — sans casser la reference du Twilio Call.
            (call as { __displayName?: string }).__displayName = cleanProspectName;
            // The prospect's list, found by the server in every list of the organisation.
            (call as { __displayList?: string }).__displayList = call.customParameters?.get?.('prospectList') || '';
            (call as { __displayPhone?: string }).__displayPhone = cleanProspectPhone || rawFrom;
            // 2026-09-27, id du ParallelCall que /voice-handler a deja cree
            // cote serveur des la sonnerie. Sans lui, l'acceptation ci-apres
            // (useIncomingCallSession.handleAccept) recreerait une 2e ligne
            // au lieu de mettre a jour celle qui existe deja.
            (call as { __parallelCallId?: string }).__parallelCallId = call.customParameters?.get?.('parallelCallId') || '';
            setIncomingCallConnection(call);

            // 2026-06-04 Kevin demo : sonnerie REPETITIVE discrete pendant
            // toute la phase de ringing inbound. Sons Twilio coupes via
            // device.audio.incoming(false) au boot, donc on est le SEUL
            // emetteur audio. Pattern : 1 "ding" doux (880 Hz, 0.18s) toutes
            // les 1.5s pendant max 25s, ou jusqu'a accept/decline/disconnect.
            const externalCallSid = (call.parameters?.CallSid || '') as string;
            let ringingTimer: ReturnType<typeof setInterval> | null = null;
            let ringingStopped = false;
            const stopRingingPattern = () => {
              if (ringingTimer) { clearInterval(ringingTimer); ringingTimer = null; }
              ringingStopped = true;
            };
            const playRingDing = () => {
              if (ringingStopped) return;
              // #23 -- "supprimer le son parasite lors de la connexion". Cette
              // sonnerie repetitive (880 Hz toutes les 1,5 s) etait le SEUL
              // emetteur audio de l'app a n'etre garde par aucun reglage :
              // `playDialBeep` et `playConnectionBeep` respectent tous deux
              // `soundEnabled`, pas elle. Mesure a l'appui : 2 oscillateurs
              // emis pendant une sonnerie simulee.
              if (!sonEstActif()) return;
              try {
                const Ctor = window.AudioContext || (window as any).webkitAudioContext;
                if (!Ctor) return;
                const ctx = new Ctor();
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.type = 'sine';
                o.frequency.value = 880;
                g.gain.value = 0.0001;
                g.gain.exponentialRampToValueAtTime(0.035, ctx.currentTime + 0.01);
                g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.20);
                o.connect(g).connect(ctx.destination);
                o.start(ctx.currentTime);
                o.stop(ctx.currentTime + 0.24);
                setTimeout(() => { ctx.close().catch(() => undefined); }, 500);
              } catch (_e) { /* best-effort */ }
            };
            playRingDing(); // 1er ding immediat
            ringingTimer = setInterval(playRingDing, 1500);
            // Stop apres 25s max (timeout TwiML <Dial timeout="20">)
            setTimeout(stopRingingPattern, 25000);

            const externalIncomingAt = Date.now();
            const cleanup = () => {
              setIncomingCallConnection(null);
              stopRingingPattern();
              // 2026-06-04 (Kevin demo bug) — On passe le CallSid (external_call_id)
              // au callback, pas une string vide. Sans ca, le DialerContext ne
              // peut pas identifier le ParallelCall a fermer si connectedContactRef
              // n'est pas encore set (race au raccrochage prospect tres rapide).
              try {
                onIncomingCallDisconnected(externalCallSid, Date.now() - externalIncomingAt);
              } catch (e) {
                console.warn('[Device] external incoming disconnect cb failed', e);
              }
            };
            // 2026-06-04 — accept stoppe aussi le ringing pattern (l'agent decroche).
            call.on('accept', stopRingingPattern);
            call.on('disconnect', cleanup);
            call.on('cancel', cleanup);
            // Ajout filet de sécurité : si le SDK reçoit une erreur réseau
            // ou si le call passe en 'closed' silencieusement, on libère.
            call.on('error', cleanup);
          }
        });

        device.register();
        tokenRefreshInterval = setInterval(refreshToken, 50 * 60 * 1000);
      } catch (error: any) {
        if (!cancelled) {
          setDeviceState('error');
          setDeviceError(error.message);
          // The init fails on the token fetch or the SDK setup before any
          // connection exists: the user-facing cause is the token.
          setDeviceErrorCode('TOKEN_FAILED');
          setDeviceReady(false);
        }
      }
    };

    const timer = setTimeout(initDevice, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (tokenRefreshInterval) clearInterval(tokenRefreshInterval);
      if (removeDialerEventListener) removeDialerEventListener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callerEmail, micPermissionGranted]);
}
